<template>
  <div class="account-view flex flex-col">
    <page-header :title="tr('Account')">
      <template #buttons>
        <ui-button :disabled="isSaving" @click="() => save()">
          <loader-circle-icon v-if="isSaving" class="animate-spin" />
          {{ tr('Save') }}
        </ui-button>
      </template>
    </page-header>

    <div class="mx-auto flex w-full max-w-3xl flex-col gap-5 py-5">
      <form-section :title="tr('Basic informations')">
        <div class="flex gap-4 max-sm:flex-col">
          <div v-for="field in nameFields" :key="field.key" class="flex flex-col gap-1.5 flex-1">
            <ui-label :for="'account-' + field.key">{{ field.label }}</ui-label>
            <ui-input
              :id="'account-' + field.key"
              v-model="$data[field.key]"
              :autocomplete="field.autocomplete"
              :aria-invalid="errorField === field.key || null"
            />
          </div>
        </div>
      </form-section>

      <form-section :title="tr('Login details')">
        <div class="flex flex-col gap-1.5">
          <ui-label for="account-email">{{ tr('E-mail') }}</ui-label>
          <ui-input
            id="account-email"
            v-model="email"
            type="email"
            autocomplete="email"
            :aria-invalid="errorField === 'email' || null"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Password') }}</ui-label>
          <ui-button variant="outline" class="self-start" @click="() => openChangePasswordModal()">
            <key-round-icon />
            {{ tr('Change password') }}
          </ui-button>
        </div>

        <div class="flex flex-col gap-2">
          <ui-label>{{ tr('Two-factor authentication') }}</ui-label>
          <label :class="[ 'flex items-center gap-3 text-sm text-foreground', disabled2faCheckbox ? 'cursor-not-allowed opacity-60' : 'cursor-pointer' ]">
            <ui-switch
              :model-value="!!$root.loggedInUser.google_2fa_enabled"
              :disabled="disabled2faCheckbox"
              @update:model-value="() => toggle2fa()"
            />
            {{ $root.loggedInUser.google_2fa_enabled ? tr('Google 2FA enabled') : tr('Enable Google 2FA') }}
          </label>
        </div>

        <ui-alert v-if="disabled2faCheckbox">
          <info-icon />
          <ui-alert-description>
            {{ tr('For users in the account, the use of two-factor authentication is teasing.') }}
          </ui-alert-description>
        </ui-alert>
      </form-section>
    </div>
  </div>
</template>

<script>
import { Info, KeyRound, LoaderCircle } from '@lucide/vue';

import ChangePasswordModal from '@/ui/components/ChangePasswordModal.vue';
import FormSection from '@/ui/components/FormSection.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import ToggleGoogle2faModal from '@/ui/components/ToggleGoogle2faModal.vue';
import { isValidEmail } from '@/utils/validators';

export default {
  name: 'AccountView',
  components: {
    FormSection,
    InfoIcon: Info,
    KeyRoundIcon: KeyRound,
    LoaderCircleIcon: LoaderCircle,
    PageHeader,
  },
  data:() => ({
    isSaving: false,
    firstname: '',
    lastname: '',
    email: '',
    errorField: '',
  }),
  computed: {
    loggedinUser() {
      return this.$root.loggedInUser;
    },
    // Hungarian names start with the last name.
    nameFields() {
      const fields = [
        { key: 'firstname', label: this.tr('First name'), autocomplete: 'given-name' },
        { key: 'lastname', label: this.tr('Last name'), autocomplete: 'family-name' },
      ];

      return this.$root.language === 'hu' ? fields.reverse() : fields;
    },
    disabled2faCheckbox() {
      return this.$root.currentCompany.required_2fa && this.$root.loggedInUser.google_2fa_enabled;
    },
  },
  created() {
    this.email = this.loggedinUser.email ?? '';
    this.firstname = this.loggedinUser.firstname ?? '';
    this.lastname = this.loggedinUser.lastname ?? '';
  },
  methods: {
    openChangePasswordModal() {
      this.$fullscreenModal.open(
        this,
        ChangePasswordModal,
        {},
      );
    },
    save() {
      if (this.isSaving || !this.validate()) {
        return;
      }

      this.isSaving = true;

      this.$store.dispatch('auth/updateAccount', {
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
      })
        .then(resp => {
          this.$toast.success(this.tr('Successful save'));

          return resp;
        })
        .catch(err => this.error(this.tr('An error occurred while saving the account details') + ':<br/>' + err.message))
        .finally(() => (this.isSaving = false));
    },
    validate() {
      this.$toast.clear();
      this.errorField = '';

      this.firstname = this.firstname.trim();
      this.lastname = this.lastname.trim();
      this.email = this.email.trim();

      if (!this.firstname) {
        this.errorField = 'firstname';
        this.$toast.error(this.tr('Cannot leave name empty!'));
        return false;
      }
      if (!this.lastname) {
        this.errorField = 'lastname';
        this.$toast.error(this.tr('Cannot leave name empty!'));
        return false;
      }

      if (!this.email) {
        this.errorField = 'email';
        this.$toast.error(this.tr('Cannot leave e-mail empty!'));
        return false;
      }

      if (!isValidEmail(this.email)) {
        this.errorField = 'email';
        this.$toast.error(this.tr('Invalid e-mail given!'));
        return false;
      }

      return true;
    },
    toggle2fa() {
      if (this.disabled2faCheckbox) {
        return;
      }

      this.$fullscreenModal.open(
        this,
        ToggleGoogle2faModal,
        {
          enable: !this.$root.loggedInUser.google_2fa_enabled,
        },
      );
    },
  },
};
</script>
